import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { getTokenPopup, getEnvironmentVariables } from '@/helpers/auth';
import { currentDatabase } from '@/helpers/currentDatabase';

// Cache for axios-instansen
let axiosClientInstance: AxiosInstance | null = null;
let initializationPromise: Promise<AxiosInstance> | null = null;

// Lazy-initialiseringsfunksjon
async function initializeAxiosClient (): Promise<AxiosInstance> {
  // Returner eksisterende instans hvis allerede initialisert
  if (axiosClientInstance) {
    return axiosClientInstance;
  }

  // Returner eksisterende promise hvis initialisering allerede pågår
  if (initializationPromise) {
    return initializationPromise;
  }

  // Opprett ny promise for initialisering
  initializationPromise = (async (): Promise<AxiosInstance> => {
    try {
      const config = await getEnvironmentVariables();
      if (!config?.VITE_API_URL) {
        throw new Error('VITE_API_URL mangler i config!');
      }

      const instance = axios.create({
        baseURL: `${config.VITE_API_URL}/api`,
      });

      // Request interceptor
      instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
        try {
          const tokenResponse = await getTokenPopup();
          if (!tokenResponse?.accessToken) {
            throw new Error('Mangler access token');
          }

          config.headers = config.headers || {};
          config.headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;

          // Bruk global variabel for database
          if (currentDatabase) {
            config.headers['Ics-DbName'] = currentDatabase;
          }

          return config;
        } catch (error) {
          console.error('Feil ved token-henting:', error);
          throw error;
        }
      });

      // Response interceptor for feilhåndtering
      instance.interceptors.response.use(
        (response) => response,
        (error) => {
          console.error('API-forespørsel feilet:', error);
          return Promise.reject(error);
        },
      );

      axiosClientInstance = instance;
      return instance;
    } finally {
      initializationPromise = null;
    }
  })();

  return initializationPromise;
}

const createAxiosClientProxy = (): AxiosInstance => {
  const wrapMethod = (methodName: string) => {
    return function (this: any, ...args: any[]) {
      if (axiosClientInstance) {
        const method = axiosClientInstance[methodName as keyof AxiosInstance] as any;
        return method.apply(axiosClientInstance, args);
      }

      return initializeAxiosClient().then((instance) => {
        const method = instance[methodName as keyof AxiosInstance] as any;
        return method.apply(instance, args);
      });
    };
  };

  return new Proxy({} as AxiosInstance, {
    get (target, prop: string | symbol) {
      const propName = String(prop);

      if (axiosClientInstance) {
        const value = axiosClientInstance[propName as keyof AxiosInstance];
        if (typeof value === 'function') {
          return value.bind(axiosClientInstance);
        }
        return value;
      }

      if (['get', 'post', 'put', 'delete', 'patch', 'head', 'options', 'request'].includes(propName)) {
        return wrapMethod(propName);
      }

      if (axiosClientInstance) {
        return axiosClientInstance[propName as keyof AxiosInstance];
      }

      return undefined;
    },
  });
};

export async function getAxiosClient (): Promise<AxiosInstance> {
  return await initializeAxiosClient();
}

export default createAxiosClientProxy();
